import useScrollAnimation from '../../../hooks/useScrollAnimation';
import useCountdown from '../../../hooks/useCountdown';

const bonuses = [
  {
    icon: 'ri-file-pdf-line',
    title: 'Guía PDF: "Cómo auditar tu inventario en 30 minutos"',
    desc: 'Un paso a paso práctico para que hagas un conteo exacto de tu bodega esta misma semana.',
    value: 'S/20',
    color: 'red',
    img: 'https://readdy.ai/api/search-image?query=professional%20PDF%20ebook%20guide%20cover%20inventory%20audit%20checklist%20minimalist%20white%20clean%20design%20business%20document%20digital%20download&width=280&height=180&seq=stock007&orientation=landscape',
  },
  {
    icon: 'ri-table-line',
    title: 'Plantilla de carga masiva para todos tus productos',
    desc: 'Sube todos tus productos en segundos. Sin teclear uno por uno. Diseñada para que funcione de primera.',
    value: 'S/20',
    color: 'emerald',
    img: 'https://readdy.ai/api/search-image?query=spreadsheet%20template%20bulk%20upload%20product%20import%20data%20table%20organized%20clean%20modern%20professional%20business%20tool%20digital%20document%20blue%20green&width=280&height=180&seq=stock008&orientation=landscape',
  },
];

const TimeBlock = ({ value, label }: { value: string; label: string }) => (
  <div className="flex flex-col items-center">
    <div className="bg-gray-900 text-white text-xl sm:text-2xl font-black w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-xl" style={{ fontFamily: "'Syne', sans-serif" }}>
      {value}
    </div>
    <span className="text-xs text-gray-500 mt-1 font-medium">{label}</span>
  </div>
);

const paymentMethods = [
  {
    name: 'Yape',
    icon: 'ri-smartphone-line',
    color: 'bg-violet-100 text-violet-700 border-violet-200',
    iconColor: 'text-violet-600',
    desc: 'Envía S/20 escaneando el QR o al número registrado',
  },
  {
    name: 'BCP',
    icon: 'ri-bank-line',
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    iconColor: 'text-amber-600',
    desc: 'Transferencia o depósito a cuenta BCP',
  },
  {
    name: 'Plin',
    icon: 'ri-send-plane-line',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    iconColor: 'text-emerald-600',
    desc: 'Pago rápido desde cualquier banco con Plin',
  },
];

const CheckoutSection = () => {
  const { ref, visible } = useScrollAnimation();
  const { hours, minutes, seconds } = useCountdown();

  return (
    <section id="checkout" className="py-16 sm:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div ref={ref} className={`text-center mb-10 sm:mb-12 ${visible ? 'anim-fade-up' : 'scroll-hidden'}`}>
          <span className="inline-block bg-amber-50 text-amber-600 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            ¡Oferta Especial!
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-gray-900 leading-tight mb-4" style={{ fontFamily: "'Syne', sans-serif" }}>
            Compra hoy y llévate estos{' '}
            <span className="text-amber-500">Bonos Exclusivos</span>
          </h2>
          <p className="text-gray-500 text-sm sm:text-base mb-6">Solo por las próximas 24 horas:</p>

          {/* Countdown */}
          <div className="flex items-center justify-center gap-2 sm:gap-3">
            <TimeBlock value={hours} label="Horas" />
            <span className="text-xl sm:text-2xl font-black text-gray-400 mb-4">:</span>
            <TimeBlock value={minutes} label="Minutos" />
            <span className="text-xl sm:text-2xl font-black text-gray-400 mb-4">:</span>
            <TimeBlock value={seconds} label="Segundos" />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-10 items-start">
          {/* Left: Bonuses + summary */}
          <div className={`flex flex-col gap-4 sm:gap-5 ${visible ? 'anim-fade-up-1' : 'scroll-hidden'}`}>
            <p className="text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wider">Bonos incluidos en tu compra:</p>
            {bonuses.map((b) => (
              <div
                key={b.title}
                className="relative bg-white border border-dashed border-gray-200 rounded-2xl overflow-hidden flex"
              >
                <div className="absolute top-3 right-3 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  GRATIS — Valor {b.value}
                </div>

                <div className="w-28 sm:w-36 flex-shrink-0 overflow-hidden">
                  <img src={b.img} alt={b.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-4 sm:p-5 flex-1 min-w-0">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center ${b.color === 'red' ? 'bg-red-100 text-red-500' : 'bg-emerald-100 text-emerald-500'} rounded-lg mb-2 sm:mb-3`}>
                    <i className={`${b.icon} text-sm sm:text-base`} />
                  </div>
                  <h3 className="text-xs sm:text-sm font-bold text-gray-900 leading-snug mb-1 sm:mb-2 pr-16 sm:pr-20">{b.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed hidden sm:block">{b.desc}</p>
                </div>
              </div>
            ))}

            {/* Total value summary */}
            <div className="bg-gray-50 rounded-2xl p-4 sm:p-5 border border-gray-100">
              <div className="flex justify-between text-xs sm:text-sm text-gray-500 mb-2">
                <span>StockSimple</span>
                <span className="line-through text-gray-400">S/149</span>
              </div>
              <div className="flex justify-between text-xs sm:text-sm text-gray-500 mb-2">
                <span>Bono 1 — Guía PDF</span>
                <span className="line-through text-gray-400">S/20</span>
              </div>
              <div className="flex justify-between text-xs sm:text-sm text-gray-500 mb-3">
                <span>Bono 2 — Plantilla</span>
                <span className="line-through text-gray-400">S/20</span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                <div>
                  <span className="font-bold text-gray-800 text-sm sm:text-base">Total HOY</span>
                  <p className="text-xs text-red-500 font-semibold mt-0.5">¡Ahorras S/169!</p>
                </div>
                <span className="text-2xl sm:text-3xl font-black text-emerald-500">S/20</span>
              </div>
            </div>
          </div>

          {/* Right: Payment methods */}
          <div className={`${visible ? 'anim-fade-up-2' : 'scroll-hidden'}`}>
            <div className="bg-white border-2 border-emerald-200 rounded-3xl p-6 sm:p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-emerald-100 rounded-xl flex-shrink-0">
                  <i className="ri-secure-payment-line text-emerald-600 text-lg sm:text-xl" />
                </div>
                <h3 className="text-base sm:text-xl font-black text-gray-900 leading-snug" style={{ fontFamily: "'Syne', sans-serif" }}>
                  SÍ, QUIERO EL ACCESO<br className="sm:hidden" /> POR S/20
                </h3>
              </div>
              <p className="text-gray-500 text-xs sm:text-sm mb-6 sm:mb-7 leading-relaxed">
                Elige tu método de pago favorito y escríbenos por WhatsApp con tu comprobante para activar tu acceso al instante.
              </p>

              {/* Payment methods */}
              <div className="flex flex-col gap-3 sm:gap-4 mb-6 sm:mb-7">
                {paymentMethods.map((pm) => (
                  <div
                    key={pm.name}
                    className={`flex items-center gap-3 sm:gap-4 border rounded-2xl p-3 sm:p-4 ${pm.color}`}
                  >
                    <div className={`w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl bg-white/70 flex-shrink-0 ${pm.iconColor}`}>
                      <i className={`${pm.icon} text-xl sm:text-2xl`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm sm:text-base">{pm.name}</p>
                      <p className="text-xs opacity-80 leading-snug">{pm.desc}</p>
                    </div>
                    <span className="text-base sm:text-lg font-black whitespace-nowrap">S/20</span>
                  </div>
                ))}
              </div>

              {/* WhatsApp CTA */}
              <a
                href="https://wa.me/?text=Hola%2C%20acabo%20de%20realizar%20mi%20pago%20de%20S%2F20%20por%20StockSimple%20y%20adjunto%20mi%20comprobante."
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm sm:text-base py-3 sm:py-4 rounded-xl transition-all cursor-pointer anim-pulse-green flex items-center justify-center gap-2 leading-snug"
              >
                <i className="ri-whatsapp-line text-lg sm:text-xl flex-shrink-0" />
                <span>Enviar comprobante<br className="sm:hidden" /> por WhatsApp</span>
              </a>

              <div className="mt-4 sm:mt-5 flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <i className="ri-shield-check-line text-emerald-500" />
                  Pago 100% seguro
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-refund-2-line text-emerald-500" />
                  Garantía 7 días
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-emerald-500" />
                  Acceso inmediato
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CheckoutSection;
